import * as THREE from 'three';
import { Spring, clamp01, easeOutBack } from '../engine/spring';
import type { Fx } from '../fx/Fx';
import type { Glows } from '../fx/Glows';
import { MergeGuide } from '../fx/MergeGuide';
import { Shape } from '../fx/Particles';
import { Projectiles } from '../fx/Projectiles';
import { fxUniforms } from '../fx/common';
import { runeFlash, type Altar, type Cell, type WorldInfo } from '../scene/World';
import { summonCost, type PlayerState, type SimEvent } from '@orb/sim';
import { KIND_COLOR, KIND_NAME, LEVEL_NAME, MAX_LEVEL, L, WILD, relicDetail, type Kind } from './config';
import type { Driver } from './Driver';
import { Relic, type RelicDeps } from './Relic';
import { Totems, TOTEMS, type TotemKind } from './Totems';

const PIPS = MAX_LEVEL;
const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _s = new THREE.Vector3(), _p = new THREE.Vector3(), _v = new THREE.Vector3(), _c = new THREE.Color();
const BLACK = new THREE.Color(0);
/** The first thing to do, said for the hand that does it (a pointer clicks, with Space too; a finger taps). */
const TAP_SEAL = typeof matchMedia === 'function' && matchMedia('(hover: hover) and (pointer: fine)').matches
  ? 'Клацніть печатку або натисніть Space — на вільну лунку прилетить сфера.'
  : 'Торкніться печатки — на вільну лунку прилетить сфера.';
const _up = new THREE.Vector3(0, 1, 0);
const PIP_WHITE = new THREE.Color(0xfff2de), INVALID = new THREE.Color(0xff634d), _flashColor = new THREE.Color();

export interface BoardFeedback { title: string; detail: string; color: number; tone: 'idle' | 'ready' | 'invalid' | 'success'; version: number }

interface Flight { from: THREE.Vector3; to: THREE.Vector3; cell: Cell; kind: Kind; t: number; dur: number; body: number; prev: THREE.Vector3 }
/** A merge under way: the absorbed stone, held until the simulation releases the new grade. */
interface Merging { a: Relic; from: number }
interface Task { at: number; fn: () => void }

/**
 * The player's altar as seen and touched: drag-to-merge, the summoning seal, level pips, buff
 * links and totems. What actually happens on the board is decided by the simulation: touches
 * become actions, and its events drive every animation here. Owns both boards' relic views.
 */
export class Board {
  readonly mine: (Relic | null)[];
  readonly totems: Totems;
  readonly partnerTotems: Totems | null;
  /** Partner's relics by cell (co-op) — they fight too. */
  readonly partner: (Relic | null)[] = [];
  private readonly merging = new Map<number, Merging>();
  /** A dropped merge waiting for the simulation's verdict. */
  private pendingMerge: { from: number; to: number; relic: Relic } | null = null;
  /** A sphere dropped on the shatter totem, waiting for the simulation's verdict. */
  private pendingShatter: { cell: number; relic: Relic } | null = null;
  /** The dragged sphere is over the player's shatter totem (main.ts lights the totem). */
  overTotem = false;
  /** Relics animating out of the board (absorbed in a merge). */
  private transient = new Set<Relic>();
  private flights: Flight[] = [];
  /** Summoned stones in flight, drawn with the elemental projectile silhouettes. */
  private readonly bodies = new Projectiles(8);
  private tasks: Task[] = [];
  private time = 0;
  readonly feedback: BoardFeedback = { title: 'Призивай сфери', detail: TAP_SEAL, color: 0xc8964e, tone: 'idle', version: 0 };
  private feedbackUntil = 0;
  private idleCheck = 0;
  private pairCheck = 0;
  private paired = new Set<Relic>();

  /* drag */
  private drag: Relic | null = null;
  private dragFrom = -1;
  private hover = -1;
  private readonly guide: MergeGuide;
  private readonly previewRune: number;
  private previewVisible = false;
  private trailClock = 0;
  private readonly dragTrail = new THREE.Vector3();

  /* per-cell visuals */
  private readonly frameFlash: Float32Array;
  private readonly pipBorn: Float32Array;
  private readonly pipDirty: Uint8Array;
  private readonly pipGlows: number[] = [];
  private readonly pips: THREE.InstancedMesh;
  private readonly linkGlows: number[][] = [];
  private readonly linkLit: boolean[] = [];

  /* summoning */
  private readonly wellGlows: number[] = [];
  private tetherT = 0;
  private readonly wellLit: number[] = [];
  private readonly arcFill = new Spring(0, 0, 120, 16);
  private readonly starSpin = new Spring(0, 0, 60, 9);
  private sealFlash = 0;
  /** Starts charged, so boot does not celebrate a charge nobody waited for. */
  private wasReady = true;
  private readyPulse = 0;
  /** Pointer hover without a press (desktop): the relic leans toward the cursor. */
  private hoverCell = -1;
  private sealHover = new Spring(0, 0, 220, 20);


  constructor(
    private readonly driver: Driver,
    private readonly world: WorldInfo,
    private readonly deps: RelicDeps,
    private readonly fx: Fx,
    private readonly glows: Glows,
  ) {
    const A = world.myAltar;
    const players = driver.state.players, me = driver.me;
    this.totems = new Totems(() => this.driver.state.players[me]);
    this.mine = A.cells.map(() => null);
    this.frameFlash = new Float32Array(A.cells.length);
    this.pipBorn = new Float32Array(A.cells.length * PIPS).fill(Infinity);
    this.pipDirty = new Uint8Array(A.cells.length).fill(1);
    this.guide = new MergeGuide(deps.scene);
    deps.scene.add(this.bodies.mesh);
    this.previewRune = deps.pool.runes.alloc();

    const pipMat = new THREE.MeshBasicMaterial();
    // rank studs: faceted diamonds set into the well's front rim, readable even at phone size
    this.pips = new THREE.InstancedMesh(new THREE.OctahedronGeometry(0.05, 0), pipMat, A.cells.length * PIPS);
    this.pips.frustumCulled = false;
    this.pips.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < A.cells.length * PIPS; i++) { this.pips.setMatrixAt(i, _m.makeScale(0, 0, 0)); this.pips.setColorAt(i, BLACK); this.pipGlows.push(glows.add(0, 0, -10, 0, { size: 0.24, opacity: 0 })); }
    deps.scene.add(this.pips);

    for (const L of A.links) {
      const g: number[] = [];
      for (let k = 0; k < 3; k++) g.push(glows.add(0xffb050, L.x + (L.vertical ? 0 : (k - 1) * L.len / 3), L.y + 0.02, L.z + (L.vertical ? (k - 1) * L.len / 3 : 0), { size: 0.42, opacity: 0 }));
      this.linkGlows.push(g); this.linkLit.push(false);
    }

    players[me].cells.forEach((c, i) => { if (c.phase === 'idle') this.place(i, c.kind, c.level, false); });

    const ally = world.partnerAltar && players.length > 1 ? 1 - me : -1;
    this.partnerTotems = ally >= 0 ? new Totems(() => this.driver.state.players[ally]) : null;
    if (ally >= 0) {
      world.partnerAltar!.cells.forEach((C, i) => {
        const c = players[ally].cells[i];
        const r = c && c.phase === 'idle' ? this.allyRelic(C, c.kind, c.level) : null;
        // the starting board is simply there, like ours (no rise-in)
        if (r) { r.scale.snap(1); r.rise.snap(0); }
        this.partner.push(r);
      });
    }
    for (const C of A.cells) { this.wellGlows.push(deps.floorGlows.add(0xff6a2a, C.x, C.floorY + 0.006, C.z, { size: C.w * 0.95, opacity: 0 })); this.wellLit.push(-1); }
    this.refreshLinks();
  }

  get altar(): Altar { return this.world.myAltar; }
  private get me(): PlayerState { return this.driver.player; }
  get sp() { return this.me.sp; }
  get cost() { return summonCost(this.me); }

  /** The relic view standing in a player's well, if any. */
  relicAt(player: number, cell: number): Relic | null {
    return player === this.driver.me ? this.mine[cell] ?? null : this.partner[cell] ?? null;
  }

  private allyRelic(C: Cell, kind: Kind, level: number) { return new Relic(this.deps, C, kind, level, { dim: true, scale: 0.72 }); }
  /** Idle in the simulation (not mid-merge, mid-rite or still flying in). */
  private simIdle(i: number) { return this.me.cells[i]?.phase === 'idle'; }

  /* ---------------- placement ---------------- */

  private place(i: number, kind: Kind, level: number, appear: boolean): Relic {
    const r = new Relic(this.deps, this.altar.cells[i], kind, level);
    this.mine[i] = r;
    if (appear) r.appear(); else r.scale.snap(1), r.rise.snap(0);
    this.setPips(i, kind, level, appear ? 0.12 : -10);
    return r;
  }

  private setPips(i: number, kind: Kind | null, level: number, delay: number) {
    for (let k = 0; k < PIPS; k++) this.pipBorn[i * PIPS + k] = kind && k < level ? this.time + (delay < 0 ? -1 : delay + k * 0.06) : Infinity;
    this.pipKind[i] = kind;
    this.pipLevel[i] = kind ? level : 0;
    this.pipDirty[i] = 1;
  }
  private pipKind: (Kind | null)[] = [];
  private pipLevel: number[] = [];

  /** True while relics are moving (shadow map needs refreshing). */
  get busy(): boolean { return this.time < this.busyUntil; }
  private busyUntil = 1;

  relics(): Relic[] { return this.mine.filter((r): r is Relic => !!r && r.state !== 'absorb' && r.state !== 'gone'); }

  /** Soul delivered to the seal. */
  private feed() {
    this.sealFlash = Math.max(this.sealFlash, 0.6);
    this.starSpin.kick(2);
  }

  inspectTotem(index: number) {
    const totem = index === 0 ? this.totems : index === 1 ? this.partnerTotems : null;
    if (!totem) return;
    const t = TOTEMS[totem.kind];
    this.showFeedback(index === 0 ? t.name : `${t.name} союзника`, index === 0 ? t.detail : t.detail.replaceAll('ваших', 'союзних'), t.color, 'ready', 4);
  }

  equipTotem(kind: TotemKind) {
    if (kind !== this.totems.kind) this.driver.send({ type: 'equip_totem', kind });
  }

  private refreshLinks() {
    this.altar.links.forEach((L, li) => {
      const a = this.mine[L.a], b = this.mine[L.b];
      const ok = (x: Relic | null) => x && x.state !== 'drag' && x.state !== 'hold' && x.state !== 'absorb';
      const lit = !!(ok(a) && ok(b) && ((a!.kind === 'y' && 'rb'.includes(b!.kind)) || (b!.kind === 'y' && 'rb'.includes(a!.kind))));
      if (lit && !this.linkLit[li]) {
        // the link "ignites": quick spark run along the groove
        _p.set(L.x, L.y + 0.03, L.z);
        this.fx.sparks(_p, 0xffd98c, 8, 1.2, { up: 0.6, life: 0.3, size: 0.025 });
        this.fx.pop(_p, 0xffc860, 0.5, 0.25, 2);
      }
      this.linkLit[li] = lit;
      // the flow runs from the sun relic: flip the length axis when the sun sits at the far end
      const dir = lit && a!.kind === 'y' ? 1 : -1;
      L.mesh.setMatrixAt(L.i, lit ? _m.compose(_p.set(L.x, L.y, L.z), _q.identity(), _s.set(L.vertical ? 1 : L.len * dir, 1, L.vertical ? L.len * dir : 1)) : _m.makeScale(0, 0, 0));
      L.mesh.instanceMatrix.needsUpdate = true;
    });
  }

  /* ---------------- input ---------------- */

  cellAt(x: number, z: number): number {
    const A = this.altar;
    let best = -1, bd = Infinity;
    for (const C of A.cells) {
      const dx = Math.abs(x - C.x) / (C.w * 0.5 + 0.12), dz = Math.abs(z - C.z) / (C.d * 0.5 + 0.12);
      const d = Math.max(dx, dz);
      if (d < 1 && d < bd) { bd = d; best = C.index; }
    }
    return best;
  }

  /** Idle pointer feedback: lift the relic under the cursor, swell the seal. */
  hoverAt(cell: number, seal: boolean) {
    const r = cell >= 0 ? this.mine[cell] : null;
    const next = r && r.state === 'idle' && this.simIdle(cell) ? cell : -1;
    if (next !== this.hoverCell && next >= 0) r!.pulse(0.35);
    this.hoverCell = next;
    this.sealHover.target = seal ? 1 : 0;
  }

  sealHit(x: number, z: number) { const s = this.world.sealPos; return Math.hypot(x - s.x, z - s.z) < 1.55 * L.sealScale; }

  canMerge(a: Relic, b: Relic | null) {
    return !!b && a !== b && b.state === 'idle' && this.simIdle(b.cell.index) && a.level === b.level && a.level < MAX_LEVEL
      && (a.kind === b.kind || a.kind === WILD || b.kind === WILD);
  }
  /** What a merge produces: the element of the pair, the prism taking the other's (two prisms stay a prism). */
  mergedKind(a: Relic, b: Relic): Kind { return a.kind === WILD ? b.kind : a.kind; }

  pointerDown(x: number, z: number): boolean {
    const i = this.cellAt(x, z), r = i >= 0 ? this.mine[i] : null;
    if (r && r.state === 'idle' && this.simIdle(i)) {
      this.drag = r; this.dragFrom = i; r.state = 'drag'; r.gemPosition(this.dragTrail);
      this.hover = i;
      r.lift.target = 0.6; r.lift.kick(6);
      r.scale.target = 1.18;
      r.pos.tune(700, 48);
      r.pulse(0.8);
      this.refreshLinks();
      this.dimPips(i, true);
      this.describeDrag(r, i);
      return true;
    }
    if (this.sealHit(x, z)) { this.summonNow(); return true; }
    return false;
  }
  /** Tap the seal (or Space on a keyboard): ask the simulation for a summon. */
  summonNow() { this.starSpin.kick(14); this.driver.send({ type: 'summon' }); }

  /** Over the player's own totem, when it is the shatter totem (a drop there breaks the sphere). */
  private totemZone(x: number, z: number) {
    const s = this.world.totemSockets[0];
    return this.totems.kind === 'shatter' && !!s && Math.hypot(x - s.x, z - s.z) < 0.8;
  }

  pointerMove(x: number, z: number) {
    const r = this.drag; if (!r) return;
    const over = this.totemZone(x, z);
    if (over !== this.overTotem) {
      this.overTotem = over;
      if (over) {
        const ready = this.totems.progress >= 1, left = Math.ceil(this.totems.period * (1 - this.totems.progress));
        if (ready) { r.pulse(1.2); r.spin.kick(-6); }
        this.showFeedback(ready ? `Розбити: ${KIND_NAME[r.kind]} · ${LEVEL_NAME[r.level - 1]}` : 'Тотем знищення заряджається',
          ready ? 'Відпустіть — сфера розіб\'ється, лунка звільниться.' : `Ще ${left} с, і тотем зможе розбити сферу.`, ready ? TOTEMS.shatter.color : 0xff634d, ready ? 'ready' : 'invalid');
      } else this.describeDrag(r, this.hover);
    }
    const i = over ? -1 : this.cellAt(x, z), t = i >= 0 ? this.mine[i] : null;
    const valid = i >= 0 && this.canMerge(r, t);
    if (i !== this.hover) {
      // the stone under the finger answers: a valid partner rises to meet it, a wrong one flinches
      const prev = this.hover >= 0 ? this.mine[this.hover] : null;
      if (prev && prev !== r && prev.state === 'idle') prev.lift.target = 0;
      if (valid) { t!.pulse(1); r.pulse(0.5); t!.lift.target = 0.14; }
      else if (t && t !== r && t.state === 'idle') { t.scale.kick(-4); t.spin.kick(-3); }
      if (this.hover >= 0) this.pipDirty[this.hover] = 1;
      this.hover = i;
      if (i >= 0) this.pipDirty[i] = 1;
      this.describeDrag(r, i);
    }
    // finger never hides the gem: hold it slightly "above" the touch point; snap magnetically onto valid targets
    let tx = x, tz = z - 0.32;
    if (valid) {
      // hover beside the partner on the side it came from, never on top of it, so both stones stay visible
      const C = this.altar.cells[i], dx = tx - C.x, dz = tz - C.z, d = Math.hypot(dx, dz) || 1;
      tx = C.x + dx / d * 0.34; tz = C.z + dz / d * 0.34;
    }
    r.pos.set(tx, r.restY, tz);
  }

  pointerUp(cancelled = false) {
    const r = this.drag; if (!r) return;
    this.drag = null;
    const over = this.hover >= 0 ? this.mine[this.hover] : null;
    if (over && over !== r && over.state === 'idle') over.lift.target = 0;
    const target = this.hover >= 0 ? this.mine[this.hover] : null;
    this.dimPips(this.dragFrom, false);
    const shatter = !cancelled && this.overTotem && this.totems.progress >= 1;
    this.overTotem = false;
    if (shatter) {
      // the stone hangs over the totem until the simulation answers
      r.state = 'hold';
      this.pendingShatter = { cell: this.dragFrom, relic: r };
      this.driver.send({ type: 'shatter', cell: this.dragFrom });
    } else if (!cancelled && target && this.canMerge(r, target)) {
      // the stone waits where it was dropped until the simulation answers (next tick solo, a turn online)
      r.state = 'hold';
      this.pendingMerge = { from: this.dragFrom, to: this.hover, relic: r };
      this.driver.send({ type: 'merge', from: this.dragFrom, to: this.hover });
    } else {
      this.returnHome(r, cancelled);
      if (!cancelled && this.hover >= 0 && this.hover !== this.dragFrom) this.frameFlash[this.hover] = 0.35;
    }
    if (this.hover >= 0) this.pipDirty[this.hover] = 1;
    this.hover = -1; this.dragFrom = -1;
    this.guide.mesh.visible = false;
    this.deps.pool.runes.set(this.previewRune, 0, -10, 0, 0, BLACK, 0, 0);
    this.refreshLinks();
  }

  private dimPips(i: number, dim: boolean) { this.pipDim[i] = dim ? 1 : 0; this.pipDirty[i] = 1; }
  private pipDim: number[] = [];

  /**
   * What the caption says between events, by the board: an empty one asks for a summon, a pair asks to be
   * merged, singles ask for more (a full board without a pair: merge is impossible, say so).
   */
  private idleHint(): [string, string] {
    const rs = this.relics().filter((r) => r.state === 'idle');
    if (!rs.length) return ['Призивай сфери', TAP_SEAL];
    if (rs.some((a) => rs.some((b) => this.canMerge(a, b)))) return ['Злий пару', 'Перетягніть сферу на таку саму — того ж рангу або Призму. Ранг зросте.'];
    if (rs.length >= this.mine.length) return ['Поле заповнене', this.totems.kind === 'shatter'
      ? 'Пари немає. Перетягніть зайву сферу на тотем знищення, щойно він зарядиться, — лунка звільниться.'
      : 'Пари немає — сфери б\'ються далі. Тотем піднесення підніме найслабшу, а тотем знищення звільнить лунку.'];
    return ['Збери пару', 'Дві однакові сфери одного рангу зливаються в одну сильнішу. Призивайте ще.'];
  }
  private showIdle() {
    const [title, detail] = this.idleHint();
    if (this.feedback.tone === 'idle' && this.feedback.title === title && this.feedback.detail === detail) return;
    this.showFeedback(title, detail, 0xc8964e, 'idle');
  }

  private showFeedback(title: string, detail: string, color: number, tone: BoardFeedback['tone'], duration = 0) {
    Object.assign(this.feedback, { title, detail, color, tone, version: this.feedback.version + 1 });
    this.feedbackUntil = duration ? this.time + duration : 0;
  }

  private describeDrag(r: Relic, i: number) {
    const target = i >= 0 ? this.mine[i] : null;
    if (this.canMerge(r, target)) {
      const into = this.mergedKind(r, target!);
      this.showFeedback(`${KIND_NAME[into]} ${LEVEL_NAME[r.level - 1]} → ${LEVEL_NAME[r.level]}`, r.kind === WILD || target!.kind === WILD ? 'Призма прийме стихію партнера і підніме її рівень.' : 'Відпустіть — обидві сфери стануть однією сильнішою.', KIND_COLOR[into], 'ready');
    } else if (target && target !== r) {
      const reason = r.level === MAX_LEVEL || target.level === MAX_LEVEL ? 'V — максимальний рівень.' : target.kind !== r.kind ? 'Оберіть сферу тієї самої стихії або Призму.' : 'Ранги обох сфер мають збігатися.';
      this.showFeedback('Злиття недоступне', reason, 0xff634d, 'invalid');
    } else {
      const count = this.mine.reduce((n, other) => n + (this.canMerge(r, other) ? 1 : 0), 0);
      const ability = relicDetail(r.kind, r.level);
      const action = count ? 'Сумісні сфери підсвічено. Перетягніть до однієї з них.' : r.level === MAX_LEVEL ? 'Легендарна сфера · найвищий ранг.' : 'Зараз немає пари. Призвіть нові сфери через печатку.';
      this.showFeedback(`${KIND_NAME[r.kind]} · ${LEVEL_NAME[r.level - 1]}`, ability ? `${ability} ${count ? 'Сумісні сфери підсвічено.' : r.level === MAX_LEVEL ? 'Максимальний рівень.' : 'Знайдіть пару для злиття.'}` : action, r.colorHex, count ? 'ready' : 'idle');
    }
  }

  /* ---------------- merge ---------------- */

  /**
   * Grade-up. Same kind + same grade → the stone evolves to the next grade.
   * Every grade escalates the whole sequence: longer charge, harder hit-stop,
   * bigger shockwaves, stronger bloom and camera punch. Grade 5 adds slow
   * motion, a crown of light shafts and the whole board resonating.
   */
  /** The simulation accepted a merge: absorb and charge, until it releases the new grade (`merged`). */
  private mergeStart(from: number, to: number) {
    const a = this.mine[from], b = this.mine[to];
    if (this.pendingMerge && this.pendingMerge.from === from) this.pendingMerge = null;
    if (!a || !b) { this.resync(); return; }
    if (this.drag === a || this.drag === b) this.pointerUp(true);
    a.state = 'absorb'; a.pos.tune(900, 50); a.pos.set(b.cell.x, b.restY, b.cell.z); a.lift.target = 0.25; a.scale.target = 0.12;
    b.pulse(2); b.scale.target = 0.85; b.state = 'absorb';
    this.mine[from] = null;
    this.transient.add(a);
    this.merging.set(to, { a, from });
    this.setPips(from, null, 0, 0);
    this.refreshLinks();
    const kind = this.mergedKind(a, b), L = a.level + 1, k = (L - 1) / 4; // k: 0.25 … 1
    const col = KIND_COLOR[kind];
    const C = b.cell;
    const center = new THREE.Vector3(C.x, C.floorY + b.gemHeight + 0.03, C.z);
    const charge = 0.24 + 0.09 * (L - 2);
    this.showFeedback(`${KIND_NAME[kind]} · злиття`, `${LEVEL_NAME[L - 2]} + ${LEVEL_NAME[L - 2]} → ${LEVEL_NAME[L - 1]}`, col, 'ready');

    // 1) absorb: the fed stone dissolves into a swirl of its element, tethered to the target by an arc
    const tether = new THREE.Vector3();
    this.fx.el.mergeCharge(kind, center, L, charge);
    // 2) charge: the element gathers, a ring closes on the well, the stone rises and brightens smoothly
    b.lift.target = 0.1 + 0.08 * k;
    this.fx.p.emit({ x: C.x, y: C.topY + 0.02, z: C.z, color: _c.set(col), intensity: 1.1 + k * 0.6, life: charge + 0.02, size: 1.3 + k * 0.6, sizeEnd: 0.25, shape: Shape.Ring, fade: 0.25 });
    if (L >= 4) this.fx.p.emit({ x: C.x, y: C.topY + 0.02, z: C.z, color: _c.set(0xfff0d0), intensity: 0.6, life: charge + 0.02, size: 1.9, sizeEnd: 0.4, shape: Shape.Ring, fade: 0.25, delay: 0.04 });
    const steps = Math.round(charge / 0.04);
    for (let s = 0; s < steps; s++) this.later(0.04 * s, () => {
      b.pulse(0.3 + 0.2 * k);
      this.fx.shake(0.015 + 0.02 * k);
      this.fx.el.mergeCharge(kind, center, L, charge);
      if (a.state === 'absorb' && a.gemPosition(tether).distanceTo(center) > 0.15) this.fx.el.arc(tether, center, col, 0.025 + 0.01 * k, 2.4, 0.06);
    });
  }

  /** The merge releases: one white frame, then the element takes shape. */
  private mergeRelease(to: number, kind: Kind, L: number) {
    const m = this.merging.get(to), b = this.mine[to];
    this.merging.delete(to);
    if (!m || !b) { this.resync(); return; }
    const a = m.a, k = (L - 1) / 4, col = KIND_COLOR[kind], C = b.cell;
    const center = new THREE.Vector3(C.x, C.floorY + b.gemHeight + 0.03, C.z);
    {
      a.state = 'gone'; a.dispose(); this.transient.delete(a);
      if (L >= 5) { this.fx.stop(0.12); this.fx.slow(0.7); } else this.fx.stop(0.05 + 0.03 * (L - 2));
      this.fx.shake(0.16 + 0.08 * (L - 2));
      this.fx.bloom(0.25 + 0.25 * (L - 2));
      this.fx.zoom(0.2 + 0.18 * (L - 2), center);

      b.transform(kind, L);
      this.showFeedback(`${KIND_NAME[kind]} · ${LEVEL_NAME[L - 1]}`, relicDetail(kind, L) ?? (L === MAX_LEVEL ? 'Легендарний рівень. Усе поле відповідає на пробудження.' : 'Новий рівень: більше сили, нові грані та сильніша аура.'), col, 'success', 2.8);
      b.state = 'idle';
      b.lift.target = 0;
      b.scale.snap(0.2); b.scale.target = 1; b.scale.kick(10 + L * 2);
      b.spin.kick(5 + L * 3);
      b.pulse(0.8 + 0.2 * L);

      // the flash: brief and small, so the shapes that follow stay readable
      this.fx.pop(center, 0xffffff, 0.55 + 0.25 * k, 0.07, 3);
      this.fx.pop(center, col, 1.1 + k, 0.35, 1.6);
      this.fx.flash(center, col, 6 + L * 2.5, 3 + k * 2, 0.35 + k * 0.3);
      this.fx.shards(center, col, 6 + L * 3, 2.2 + k * 1.5, 2.4);
      this.fx.el.mergeBurst(kind, center, C.floorY, L);
      runeFlash.value.set(fxUniforms.uTime.value, 0.6 + L * 0.15);
      // shockwaves in the element colour, one thin pale ring on top
      for (let r = 0; r < L - 1; r++) this.fx.ring(_p.set(C.x, C.topY, C.z), col, 1.1 + r * 0.55 + k, 0.45 + r * 0.08, 2.4 - r * 0.3, r * 0.07);
      this.fx.ring(_p.set(C.x, C.topY, C.z), 0xfff0d0, 0.9 + k, 0.3, 1.4, 0.02);
      this.fx.floorFlash(_p.set(C.x, C.topY, C.z), col, 1.5 + k * 1.2, 0.4, 1.6);
      this.fx.beam(_p.set(C.x, C.floorY, C.z), col, 0.35 + 0.08 * L, 2 + 0.6 * L, 0.45 + k * 0.35, 1.3);
      if (L >= 3) this.fx.beam(_p.set(C.x, C.floorY, C.z), 0xfff0d0, 0.08 + 0.02 * L, 2.4 + 0.6 * L, 0.14 + k * 0.08, 1.6);
      this.fx.sparks(center, col, 16 + L * 6, 4 + k * 2.5, { up: 0.8, life: 0.5 + k * 0.2, size: 0.035, intensity: 2.6 });
      for (let f = 0; f < L - 1; f++) this.later(0.04 + f * 0.07, () => this.fx.flare(_p.set(center.x + (Math.random() - 0.5) * 0.4, center.y + Math.random() * 0.25, center.z + (Math.random() - 0.5) * 0.3), 0xfff4e0, 0.45 + k * 0.4, 0.3, 2.6));
      this.fx.embers(center, col, 8 + L * 3, { radius: 0.4, rise: 1.4, life: 1.1 });

      if (L >= 4) {
        // ascending vortex of the element's sparks around the column
        for (let i = 0, n = this.fx.p.capacity > 8192 ? 36 : 24; i < n; i++) {
          const ang = (i / n) * Math.PI * 2, rr = 0.6;
          this.fx.p.emit({ x: C.x + Math.cos(ang) * rr, y: C.topY + 0.05, z: C.z + Math.sin(ang) * rr, vx: -Math.sin(ang) * 2.2 - Math.cos(ang) * 0.4, vy: 2.5 + Math.random(), vz: Math.cos(ang) * 2.2 - Math.sin(ang) * 0.4, color: _c.set(col), intensity: 2.2, life: 0.9, size: 0.045, sizeEnd: 0, drag: 1.2, shape: Shape.Spark, stretch: 0.04, delay: i * 0.006 });
        }
        this.fx.floorFlash(_p.set(C.x, C.topY, C.z), col, 4, 0.7, 0.9);
      }
      if (L >= 5) {
        // crown of light shafts + a shockwave across the whole field
        for (let i = 0; i < 8; i++) {
          const ang = (i / 8) * Math.PI * 2;
          this.later(0.05 + i * 0.03, () => this.fx.beam(_p.set(C.x + Math.cos(ang) * 0.55, C.floorY, C.z + Math.sin(ang) * 0.42), i % 2 ? 0xfff0d0 : col, 0.2, 4.2, 0.8, 2.4));
        }
        this.fx.ring(_p.set(C.x, C.topY, C.z), col, 7, 1.0, 2.2, 0.1);
        this.fx.ring(_p.set(C.x, C.topY, C.z), 0xfff0d0, 9, 1.2, 1.2, 0.22);
        this.fx.el.mergeBurst(kind, center, C.floorY, L, 1.5);
        runeFlash.value.set(fxUniforms.uTime.value, 1.2 + L * 0.2);
        // every relic on the board answers the call
        for (const r of this.mine) if (r && r !== b) {
          const dist = Math.hypot(r.cell.x - C.x, r.cell.z - C.z);
          this.later(0.1 + dist * 0.12, () => { r.pulse(2); r.lift.kick(2); this.frameFlash[r.cell.index] = Math.max(this.frameFlash[r.cell.index], 1); });
        }
      }
      this.frameFlash[to] = 1.4 + k;
      this.setPips(to, kind, L, 0.15);
      this.refreshLinks();
    }
  }

  /** The ascend totem lifts a relic (the simulation chose it): a golden thread from the totem's heart. */
  private ascendStart(index: number, kind: Kind, L: number, rite: number) {
    const r = this.mine[index]; if (!r) { this.resync(); return; }
    if (this.drag === r) this.pointerUp(true);
    const gold = TOTEMS.ascend.color, C = r.cell, S = this.world.totemSockets[0];
    const center = new THREE.Vector3(C.x, C.floorY + r.gemHeight + 0.03, C.z), heart = new THREE.Vector3(S.x, S.y + 1.48, S.z);
    // hold the relic for the rite so it can't be dragged or merged half-way
    r.state = 'absorb'; r.lift.target = 0.12; r.pulse(1);
    this.showFeedback('Тотем піднесення', `${KIND_NAME[kind]}: ${LEVEL_NAME[L - 2]} → ${LEVEL_NAME[L - 1]}`, gold, 'ready');
    // a golden thread of light from the totem's heart to the relic, motes running along it
    const steps = Math.round(rite / 0.04);
    for (let s = 0; s < steps; s++) this.later(0.04 * s, () => {
      if (r.state !== 'absorb') return;
      this.fx.el.arc(heart, center, gold, 0.028, 2.6, 0.07);
      const f = s / steps;
      _p.lerpVectors(heart, center, f); _p.y += Math.sin(f * Math.PI) * 0.35;
      this.fx.p.emit({ x: _p.x, y: _p.y, z: _p.z, color: _c.set(0xfff0c0), intensity: 2.4, life: 0.35, size: 0.06, sizeEnd: 0, shape: Shape.Glow, fade: 1 });
      r.pulse(0.3);
    });
    this.fx.sparks(heart, gold, 12, 1.6, { up: 0.6, life: 0.4, size: 0.03, intensity: 2.4 });
  }

  /** The relic takes the new level: the same language as a merge, a size smaller. */
  private ascendRelease(index: number, kind: Kind, L: number) {
    const r = this.mine[index]; if (!r) { this.resync(); return; }
    const k = (L - 1) / 4, gold = TOTEMS.ascend.color, col = KIND_COLOR[kind], C = r.cell;
    const center = new THREE.Vector3(C.x, C.floorY + r.gemHeight + 0.03, C.z);
    this.fx.stop(0.04); this.fx.shake(0.1 + 0.05 * k); this.fx.bloom(0.3 + 0.2 * k);
    r.transform(kind, L);
    r.state = 'idle'; r.lift.target = 0;
    r.scale.snap(0.4); r.scale.target = 1; r.scale.kick(8 + L * 2); r.spin.kick(4 + L * 2); r.pulse(0.8 + 0.2 * L);
    this.fx.pop(center, 0xfff4d8, 0.5 + 0.2 * k, 0.07, 3);
    this.fx.pop(center, col, 0.9 + k, 0.3, 1.5);
    this.fx.el.mergeBurst(kind, center, C.floorY, L, 0.7);
    this.fx.ring(_p.set(C.x, C.topY, C.z), gold, 1.1 + k, 0.4, 2.2);
    this.fx.beam(_p.set(C.x, C.floorY, C.z), gold, 0.3 + 0.06 * L, 2 + 0.5 * L, 0.45, 1.4);
    this.fx.embers(center, gold, 10 + L * 2, { radius: 0.35, rise: 1.6, life: 1 });
    runeFlash.value.set(fxUniforms.uTime.value, 0.5 + L * 0.1);
    this.frameFlash[index] = 1.2 + k;
    this.setPips(index, kind, L, 0.15);
    this.refreshLinks();
    this.showFeedback(`${KIND_NAME[kind]} · ${LEVEL_NAME[L - 1]}`, relicDetail(kind, L) ?? 'Тотем піднесення підняв найслабшу сферу на ранг.', col, 'success', 2.4);
  }

  /* ---------------- summon ---------------- */

  private summonDenied(full: boolean) {
    const S = this.world.sealPos;
    // denied: dull thud, red fizzle
    this.fx.sparks(S, 0xff2a10, 10, 2, { up: 0.5, life: 0.25, size: 0.025 });
    this.fx.shake(0.08);
    this.sealFlash = -1;
    if (full) this.showFeedback('Поле заповнене', 'Злийте пару, щоб звільнити лунку.', 0xff634d, 'invalid', 2.4);
    // not enough charge is not a mistake: say how much is left and what speeds it up
    else this.showFeedback('Печатка заряджається', `Ще ${Math.max(0.1, Math.ceil((this.cost - this.sp) * 10) / 10).toLocaleString('uk-UA')} до призову. Душі переможених ворогів прискорюють заряд.`, 0xffb060, 'idle', 2.4);
  }

  /** The simulation granted a summon: the stone leaves the seal for the well it picked. */
  private summon(index: number, kind: Kind, flight: number) {
    const S = this.world.sealPos, C = this.altar.cells[index], col = KIND_COLOR[kind];
    this.showFeedback('Призов сфери', 'Енергія печатки знаходить вільну лунку.', 0xffb060, 'ready', 1.5);
    const from = S.clone().add(new THREE.Vector3(0, 0.2, 0));
    this.flights.push({ from, to: new THREE.Vector3(C.x, C.topY + 0.35, C.z), cell: C, kind, t: 0, dur: flight,
      body: this.bodies.add(kind, 1, from, _p.set(0, 1, 0), 1.1), prev: from.clone() });
    this.sealFlash = 1.5;
    runeFlash.value.set(fxUniforms.uTime.value, 0.45);
    // the seal answers instantly; the colour of the burst already tells which element is coming
    this.fx.pop(S, 0xffe0b0, 0.6, 0.08, 3);
    this.fx.pop(S, col, 1.1, 0.28, 2);
    this.fx.ring(_p.set(S.x, 0.3, S.z), 0xff8a36, 1.7, 0.45, 2.4);
    this.fx.ring(_p.set(S.x, 0.31, S.z), col, 1.1, 0.35, 2, 0.05);
    this.fx.beam(_p.set(S.x, 0.25, S.z), col, 0.35, 1.8, 0.3, 2);
    this.fx.sparks(S, 0xffa050, 12, 3.2, { up: 1.2, life: 0.4 });
    this.fx.sparks(S, col, 10, 3, { up: 1.4, life: 0.4, intensity: 2.4 });
    this.fx.el.smoke(S.x, 0.4, S.z, 0, 0.5, 0, 0.3, 0.9, 1, 0x3a2e26, 0.35);
    this.fx.flash(S, col, 6, 3, 0.3);
    this.fx.shake(0.08);
  }

  private land(index: number, kind: Kind) {
    const fi = this.flights.findIndex((f) => f.cell.index === index);
    if (fi >= 0) this.bodies.remove(this.flights.splice(fi, 1)[0].body);
    this.mine[index]?.dispose();
    const r = this.place(index, kind, 1, true);
    const C = this.altar.cells[index], col = KIND_COLOR[kind];
    _p.set(C.x, C.topY, C.z);
    // the stone lands in its well: a short white frame, then its element blooms at grade I
    this.fx.ring(_p, col, 1.1, 0.4, 2.6);
    this.fx.floorFlash(_p, col, 1.3, 0.3, 1.6);
    this.fx.dust(_p, 0.35, 5, 0.5);
    this.fx.sparks(_p.set(C.x, C.topY + 0.15, C.z), col, 14, 3.5, { up: 1.1, life: 0.42, size: 0.032, intensity: 2.4 });
    this.fx.pop(_p.set(C.x, C.topY + 0.3, C.z), 0xfff0d8, 0.55, 0.07, 3);
    this.fx.el.mergeBurst(kind, _p, C.floorY, 1, 0.65);
    this.fx.flash(_p, col, 6, 2.8, 0.3);
    this.fx.shake(0.1);
    this.frameFlash[C.index] = 1.6;
    r.pulse(3);
    this.showFeedback(`${KIND_NAME[kind]} · I`, relicDetail(kind, 1) ?? 'Сфера готова до бою. Знайдіть їй пару для злиття.', col, 'success', 2);
    this.refreshLinks();
  }

  /* ---------------- simulation events ---------------- */

  events(events: readonly SimEvent[]) {
    const me = this.driver.me;
    for (const ev of events) {
      if (!('player' in ev)) continue;
      if (ev.player !== me) { this.allyEvent(ev); continue; }
      switch (ev.type) {
        case 'soul_arrive': this.feed(); break;
        case 'summon': this.summon(ev.cell, ev.kind, ev.flight); break;
        case 'summon_denied': this.summonDenied(ev.reason === 'full'); break;
        case 'land': this.land(ev.cell, ev.kind); break;
        case 'merge': this.mergeStart(ev.from, ev.to); break;
        case 'merged': this.mergeRelease(ev.cell, ev.kind, ev.level); break;
        case 'merge_denied': {
          const p = this.pendingMerge;
          if (p && p.from === ev.from) { this.pendingMerge = null; this.returnHome(p.relic, false); }
          break;
        }
        case 'ascend': this.ascendStart(ev.cell, ev.kind, ev.level, ev.rite); break;
        case 'ascended': this.ascendRelease(ev.cell, ev.kind, ev.level); break;
        case 'shatter': this.shattered(ev.cell, ev.kind, ev.level); break;
        case 'shatter_denied': {
          const p = this.pendingShatter;
          if (p && p.cell === ev.cell) { this.pendingShatter = null; this.returnHome(p.relic, false); }
          break;
        }
        case 'totem': this.inspectTotem(0); break;
      }
    }
  }

  /** The shatter totem broke a sphere: it bursts where it hangs, its well is free. */
  private shattered(cell: number, kind: Kind, level: number) {
    if (this.pendingShatter?.cell === cell) this.pendingShatter = null;
    const r = this.mine[cell]; if (!r) return;
    const col = KIND_COLOR[kind];
    r.gemPosition(_p);
    this.fx.flare(_p, 0xffffff, 0.8, 0.12, 3);
    this.fx.sparks(_p, col, 18, 3.2, { up: 0.6, life: 0.45, size: 0.03, intensity: 2.4, gravity: 6 });
    this.fx.sparks(_p, TOTEMS.shatter.color, 10, 2.2, { up: 0.3, life: 0.35, size: 0.025, intensity: 2.2 });
    this.fx.ring(_p, TOTEMS.shatter.color, 0.9, 0.35, 2.2);
    this.fx.flash(_p, col, 5, 2.5, 0.25);
    this.fx.shake(0.08);
    r.dispose(); this.mine[cell] = null; this.setPips(cell, null, 0, 0);
    this.frameFlash[cell] = 1;
    this.showFeedback(`${KIND_NAME[kind]} · ${LEVEL_NAME[level - 1]} розбито`, 'Лунка вільна — призивайте нову сферу.', TOTEMS.shatter.color, 'success', 2);
    this.refreshLinks();
  }

  /** The ally's board changes plainly: no seal or pips on that side. */
  private allyEvent(ev: SimEvent) {
    const A = this.world.partnerAltar;
    if (!A || !('player' in ev)) return;
    if (ev.type === 'land') {
      this.partner[ev.cell]?.dispose();
      const r = this.partner[ev.cell] = this.allyRelic(A.cells[ev.cell], ev.kind, 1);
      r.appear();
    } else if (ev.type === 'merge') {
      const a = this.partner[ev.from]; this.partner[ev.from] = null;
      a?.dispose(); this.partner[ev.to]?.pulse(2);
    } else if (ev.type === 'shatter') {
      const r = this.partner[ev.cell]; this.partner[ev.cell] = null;
      if (r) { r.gemPosition(_p); this.fx.sparks(_p, KIND_COLOR[ev.kind], 12, 2.6, { up: 0.5, life: 0.4, size: 0.026 }); r.dispose(); }
    } else if (ev.type === 'merged' || ev.type === 'ascended') {
      const r = this.partner[ev.cell]; if (!r) return;
      r.transform(ev.kind, ev.level); r.scale.snap(0.3); r.scale.target = 1; r.scale.kick(10); r.pulse(2);
    }
  }

  private returnHome(r: Relic, cancelled: boolean) {
    r.state = 'return'; r.lift.target = 0; r.scale.target = 1; r.pos.tune(260, 22);
    this.later(0.16, () => { r.scale.kick(-3); this.fx.ring(_p.set(r.cell.x, r.cell.topY, r.cell.z), r.colorHex, 0.7, 0.3, 1.5); });
    this.showFeedback(cancelled ? 'Перетягування скасовано' : 'Сфера повертається', 'Для злиття потрібні однакова стихія та однаковий ранг.', r.colorHex, 'idle', 1.8);
  }

  /** Bring the views back in line with the simulation (after a restore, or a missed event). */
  resync() {
    const cells = this.me.cells;
    cells.forEach((c, i) => {
      const r = this.mine[i];
      const want = c.phase === 'idle' || c.phase === 'merging' || c.phase === 'ascending';
      if (!want) { if (r && !this.flights.some((f) => f.cell.index === i)) { r.dispose(); this.mine[i] = null; this.setPips(i, null, 0, 0); } return; }
      if (!r) { this.place(i, c.kind, c.level, false); return; }
      if (r.kind !== c.kind || r.level !== c.level) { r.transform(c.kind, c.level); this.setPips(i, c.kind, c.level, -10); }
      if (r.state === 'absorb' && c.phase === 'idle') r.state = 'idle';
    });
    // the ally's plinth too
    const A = this.world.partnerAltar, players = this.driver.state.players;
    if (A && players.length > 1) {
      players[1 - this.driver.me].cells.forEach((c, i) => {
        const r = this.partner[i], want = c.phase !== 'empty' && c.phase !== 'incoming';
        if (!want) { if (r) { r.dispose(); this.partner[i] = null; } return; }
        if (!r) { const n = this.partner[i] = this.allyRelic(A.cells[i], c.kind, c.level); n.scale.snap(1); n.rise.snap(0); return; }
        if (r.kind !== c.kind || r.level !== c.level) r.transform(c.kind, c.level);
      });
    }
    this.refreshLinks();
  }

  /* ---------------- frame ---------------- */

  private later(dt: number, fn: () => void) {
    const task = { at: this.time + dt, fn };
    let i = this.tasks.length;
    while (i > 0 && this.tasks[i - 1].at > task.at) i--;
    this.tasks.splice(i, 0, task);
  }

  update(dt: number, camera: THREE.Camera) {
    this.time += dt;
    const t = this.time;
    while (this.tasks.length && this.tasks[0].at <= t) this.tasks.shift()!.fn();
    // «заряджається» goes as soon as the charge is there
    if (this.feedbackUntil > 0 && this.feedback.title === 'Печатка заряджається' && this.sp >= this.cost) this.feedbackUntil = t;
    // between events the caption follows the board (a few times a second is plenty)
    if (!this.drag && (this.feedbackUntil > 0 ? t >= this.feedbackUntil : this.feedback.tone === 'idle' && (this.idleCheck -= dt) <= 0)) { this.idleCheck = 0.3; this.feedbackUntil = 0; this.showIdle(); }
    this.trailClock += dt;
    const trail = this.trailClock >= 1 / 60;
    if (trail) this.trailClock %= 1 / 60;

    // the seal's charge (the trickle and the souls are counted by the simulation)
    this.arcFill.target = clamp01(this.sp / this.cost);
    this.arcFill.update(dt);
    const ready = this.sp >= this.cost;
    const S = this.world.sealPos;
    if (ready && !this.wasReady) {
      // charged: a bright release that invites the tap
      this.sealFlash = Math.max(this.sealFlash, 1.2);
      this.starSpin.kick(8);
      this.fx.pop(S, 0xffc070, 1.1, 0.25, 3);
      this.fx.ring(_p.set(S.x, 0.3, S.z), 0xffb060, 1.6, 0.5, 2.6);
      this.fx.sparks(S, 0xffb060, 14, 2.6, { up: 1.4, life: 0.45, size: 0.028 });
      this.readyPulse = 1.1;
    }
    this.wasReady = ready;
    if (ready && (this.readyPulse -= dt) <= 0) {
      this.readyPulse = 1.5;
      this.fx.ring(_p.set(S.x, 0.29, S.z), 0xff9a48, 1.35, 0.9, 0.9);
      this.fx.embers(S, 0xffb060, 3, { radius: 0.5, rise: 0.8, life: 0.9, size: 0.035, intensity: 1.6 });
    }
    // empty wells are not dead pits: a faint ember at the bottom, and while the seal is charged they breathe
    // its colour in a wave, showing where the next summon can land
    this.altar.cells.forEach((_, i) => {
      const empty = this.me.cells[i]?.phase === 'empty';
      const o = !empty ? 0 : ready ? 0.16 + 0.12 * Math.sin(this.time * 3.2 - i * 0.55) : 0.07;
      if (Math.abs(o - this.wellLit[i]) < 0.004) return;
      this.wellLit[i] = o;
      this.deps.floorGlows.setColor(this.wellGlows[i], ready ? 0xffa850 : 0xff6a2a, o);
    });
    // a crackling tether between the dragged stone and a partner it can merge with
    if (this.drag && this.hover >= 0 && this.canMerge(this.drag, this.mine[this.hover]) && (this.tetherT -= dt) <= 0) {
      this.tetherT = 0.07;
      this.drag.gemPosition(_p); this.mine[this.hover]!.gemPosition(_v);
      this.fx.el.arc(_p, _v, this.drag.colorHex, 0.02, 2, 0.08);
    }
    this.sealHover.update(dt);
    const A = this.world.spArc;
    A.mat.uniforms.uFill.value = Math.max(0.001, this.arcFill.x);
    this.sealFlash += (0 - this.sealFlash) * Math.min(1, dt * 5);
    A.mat.uniforms.uFlash.value = Math.max(0, this.sealFlash) + (ready ? 0.12 + 0.12 * Math.sin(t * 5) : 0);
    A.glows.forEach((g, k) => {
      const u = k / A.glows.length;
      this.glows.setOpacity(g, u <= this.arcFill.x ? 0.28 + (ready ? 0.15 * Math.sin(t * 5 - u * 12) : 0) : 0);
    });
    this.starSpin.update(dt);
    this.world.sealStar.rotation.y += (0.15 + this.starSpin.x) * dt;
    const core = this.world.sealCore;
    (core.material as THREE.MeshPhysicalMaterial).emissiveIntensity = 1.6 + (ready ? 0.8 + 0.6 * Math.sin(t * 5) : 0) + Math.max(0, this.sealFlash) * 3;
    core.scale.setScalar(1 + Math.max(0, this.sealFlash) * 0.4 + (ready ? 0.06 * Math.sin(t * 5) : 0) + this.sealHover.x * 0.18);

    // flights (seal → cell)
    for (let i = this.flights.length - 1; i >= 0; i--) {
      const f = this.flights[i];
      f.t += dt;
      const u = clamp01(f.t / f.dur), e = u * u * (3 - 2 * u);
      _p.lerpVectors(f.from, f.to, e); _p.y += Math.sin(u * Math.PI) * 1.4;
      const col = KIND_COLOR[f.kind];
      _v.subVectors(_p, f.prev).divideScalar(Math.max(dt, 1e-4)); f.prev.copy(_p);
      this.bodies.set(f.body, _p, _v);
      if (trail && u < 1) { this.fx.trail(_p, col, 0.14, 2.2, 0.28); this.fx.el.summonTrail(f.kind, _p, _v); }
      // the landing itself is the simulation's `land`; a flight it never confirms fades out
      if (f.t > f.dur + 1) { this.bodies.remove(f.body); this.flights.splice(i, 1); }
    }
    this.bodies.flush();

    if (this.drag || this.flights.length || this.tasks.length || this.mine.some((r) => r && r.state !== 'idle')) this.busyUntil = t + 1.2;
    // between drags the relics that have a partner breathe softly, so a pair is seen, not searched for
    if ((this.pairCheck -= dt) <= 0) {
      this.pairCheck = 0.25;
      const rs = this.relics().filter((r) => r.state === 'idle');
      this.paired = new Set(rs.filter((a) => rs.some((b) => this.canMerge(a, b))));
    }
    const breathe = 0.16 + 0.12 * Math.sin(t * 3.2);
    for (const r of this.mine) if (r) {
      r.focus.target = this.drag ? r === this.drag ? 1 : this.canMerge(this.drag, r) ? r.cell.index === this.hover ? 1 : 0.35 : 0
        : r.cell.index === this.hoverCell && r.state === 'idle' ? 0.6 : this.paired.has(r) ? breathe : 0;
      r.update(dt, camera);
    }
    for (const r of this.partner) r?.update(dt, camera);
    // a fast drag leaves a fading wake, so the stone reads as carried, not teleported
    if (this.drag && trail) {
      const g = this.drag.gemPosition(_p), d2 = g.distanceToSquared(this.dragTrail);
      if (d2 > 0.0016) {
        this.fx.trail(g, this.drag.colorHex, 0.07 + Math.min(0.08, Math.sqrt(d2) * 0.4), 1.6, 0.22);
        if (d2 > 0.01) this.fx.embers(g, this.drag.colorHex, 1, { radius: 0.05, rise: 0.3, life: 0.4, size: 0.03, intensity: 2 });
        this.dragTrail.copy(g);
      }
    }
    for (const r of this.transient) {
      r.update(dt, camera);
      if (trail) this.fx.trail(r.gemPosition(_p), r.colorHex, 0.1, 1.8, 0.2);
    }

    this.updateGuide();

    this.updateFrames(dt);
    this.updatePips();
    this.updateLinks();
    this.flowLinks(dt);
  }

  private updateFrames(dt: number) {
    const A = this.altar, F = A.frames, d = this.drag;
    const pulse = 0.5 + 0.5 * Math.sin(this.time * 9);
    for (const C of A.cells) {
      const i = C.index;
      this.frameFlash[i] = Math.max(0, this.frameFlash[i] - dt * 3);
      _c.setRGB(0, 0, 0);
      if (d) {
        const r = this.mine[i];
        if (i === this.dragFrom) _c.setRGB(0.25, 0.22, 0.2);
        else if (r && this.canMerge(d, r)) _c.set(d.colorHex).multiplyScalar(i === this.hover ? 3.2 : 0.7 + pulse * 0.9);
        else if (i === this.hover) _c.copy(INVALID).multiplyScalar(0.35 + pulse * 0.25);
      }
      if (this.frameFlash[i] > 0) { const r = this.mine[i]; _c.add(_flashColor.set(r ? r.colorHex : 0xffffff).multiplyScalar(this.frameFlash[i])); }
      const a = F.instanceColor!;
      if (a.getX(i) !== Math.fround(_c.r) || a.getY(i) !== Math.fround(_c.g) || a.getZ(i) !== Math.fround(_c.b)) {
        F.setColorAt(i, _c); a.addUpdateRange(i * 3, 3); a.needsUpdate = true;
      }
    }
  }

  private updatePips() {
    const A = this.altar, t = this.time;
    let dirtyMin = Infinity, dirtyMax = -1;
    for (const C of A.cells) {
      const i = C.index, kind = this.pipKind[i], level = this.pipLevel[i] ?? 0;
      const preview = !!this.drag && i === this.hover && this.canMerge(this.drag, this.mine[i]);
      if (!this.pipDirty[i] && !preview) continue;
      const n = level + (preview ? 1 : 0);
      dirtyMin = Math.min(dirtyMin, i * PIPS); dirtyMax = Math.max(dirtyMax, (i + 1) * PIPS - 1);
      let animating = preview;
      const step = Math.min(0.14, (C.w * 0.55) / Math.max(n, 1));
      for (let k = 0; k < PIPS; k++) {
        const j = i * PIPS + k, ghost = preview && k === level, born = ghost ? t - 0.2 : this.pipBorn[j];
        const g = this.pipGlows[j];
        if (!kind || k >= n || t < born) {
          if (kind && k < n && t < born) animating = true;
          this.pips.setMatrixAt(j, _m.makeScale(0, 0, 0));
          this.glows.setOpacity(g, 0);
          continue;
        }
        const u = clamp01((t - born) / 0.28), s = easeOutBack(u, 3);
        const x = C.x + (k - (n - 1) / 2) * step, z = C.z + C.d / 2 + 0.1, y = C.topY - 0.002;
        this.pips.setMatrixAt(j, _m.compose(_p.set(x, y + 0.012 + (1 - u) * 0.08, z), _q.setFromAxisAngle(_up, Math.PI / 4), _s.set(s, s * 0.55, s)));
        if (t - born < 0.35) animating = true;
        const dim = this.pipDim[i] ? 0.35 : 1;
        const pop = Math.max(0, 1 - (t - born) / 0.35);
        this.pips.setColorAt(j, _c.set(KIND_COLOR[kind]).lerp(PIP_WHITE, ghost ? 0.6 : 0.15).multiplyScalar(dim * (ghost ? 1.2 + 0.4 * Math.sin(t * 9) : 0.95 + pop * 2.5)));
        this.glows.setPos(g, x, y + 0.02, z);
        this.glows.setColor(g, KIND_COLOR[kind], dim * (0.6 + pop * 2));
      }
      this.pipDirty[i] = animating ? 1 : 0;
    }
    if (dirtyMax >= 0) for (const a of [this.pips.instanceMatrix, this.pips.instanceColor!]) {
      a.addUpdateRange(dirtyMin * a.itemSize, (dirtyMax - dirtyMin + 1) * a.itemSize); a.needsUpdate = true;
    }
  }

  private updateGuide() {
    const target = this.hover >= 0 ? this.mine[this.hover] : null;
    const ready = !!this.drag && this.canMerge(this.drag, target);
    this.guide.mesh.visible = ready;
    if (ready) {
      this.drag!.gemPosition(this.guide.from); target!.gemPosition(this.guide.to);
      this.guide.color.copy(this.drag!.color);
      const C = target!.cell;
      this.deps.pool.runes.set(this.previewRune, C.x, C.topY + 0.016, C.z, -this.time * 0.4, this.drag!.color, 0.55 + 0.15 * Math.sin(this.time * 9), C.d * 1.12);
    } else if (this.previewVisible) this.deps.pool.runes.set(this.previewRune, 0, -10, 0, 0, BLACK, 0, 0);
    this.previewVisible = ready;
  }

  /** Sun relics feed their neighbours: golden motes run along the groove from the citrine to the buffed stone. */
  private linkFlow = 0;
  private flowLinks(dt: number) {
    if ((this.linkFlow -= dt) > 0) return;
    this.linkFlow = 0.14;
    this.altar.links.forEach((L, li) => {
      if (!this.linkLit[li]) return;
      const a = this.mine[L.a], b = this.mine[L.b];
      if (!a || !b) return;
      const src = a.kind === 'y' ? a : b, dst = src === a ? b : a;
      const y = L.y + 0.035, dx = dst.cell.x - src.cell.x, dz = dst.cell.z - src.cell.z, life = 0.42;
      const k = 0.18 + Math.random() * 0.1, side = (Math.random() - 0.5) * 0.04;
      this.fx.p.emit({ x: src.cell.x + dx * k + (L.vertical ? side : 0), y, z: src.cell.z + dz * k + (L.vertical ? 0 : side),
        vx: dx * (1 - 2 * k) / life, vy: 0, vz: dz * (1 - 2 * k) / life, color: _c.set(0xffc060), intensity: 1.6, life,
        size: 0.042, sizeEnd: 0.016, shape: Shape.Spark, stretch: 0.06, fade: 0.6 });
    });
  }

  private updateLinks() {
    const t = this.time;
    this.altar.links.forEach((L, li) => {
      const lit = this.linkLit[li];
      this.linkGlows[li].forEach((g, k) => this.glows.setOpacity(g, lit ? 0.1 + 0.12 * Math.max(0, Math.sin(t * 4 - k * 1.2)) : 0));
      void L;
    });
  }
}
